import { useEffect, useState, useRef } from 'react';
import {
    GenericPopup,
    GenericAlertPopup,
    ButtonTheme,
    DropDownBox,
    PhoneBox,
    YesNoToggleButton,
    ToggleButtonLabelSize,
} from '@apply/components';
import '../../scss/_userModal.scss';
import { isMobileScreen } from '@apply/ui';
import { AuthorizedDrawUserDto } from '@apply/api/src/api/ApplyApi';
import { GCRoleType, GCRoleTypeOptions } from '@apply/constants';
import { TextBox } from '@apply/components';
import { ValidationGroup } from 'devextreme-react';
import { ButtonGroupTypes } from 'devextreme-react/button-group';

export interface UserModalProps {
    isOpen?: boolean;
    authorizedDrawUser?: AuthorizedDrawUserDto;
    onSubmitAsync: (userToSave: Partial<AuthorizedDrawUserDto>) => Promise<boolean>;
    onClose: () => void;
}

export enum SaveInvitedUserButtonTextOptions {
    SendInvite = 'Send Invite',
    Save = 'Save',
}

export const UserModal: React.FC<UserModalProps> = ({ isOpen = false, authorizedDrawUser, onSubmitAsync, onClose }) => {
    const validationGroupRef = useRef<ValidationGroup>(null);
    const [saveButtonText, set_saveButtonText] = useState(SaveInvitedUserButtonTextOptions.SendInvite);
    const mobileScreen = isMobileScreen();
    const [submitting, set_submitting] = useState<boolean>(false);
    const [validationAttempted, set_validationAttempted] = useState<boolean>(false);
    const [formKey, set_formKey] = useState<number>(0);

    const [firstName, set_firstName] = useState<string>();
    const [lastName, set_lastName] = useState<string>();
    const [roleType, set_roleType] = useState<GCRoleType | undefined>();
    const [phone, set_phone] = useState<string>();
    const [email, set_email] = useState<string>();
    const [drawRequireApproval, set_drawRequireApproval] = useState<boolean>();

    const resetFields = () => {
        set_firstName(undefined);
        set_lastName(undefined);
        set_roleType(undefined);
        set_phone(undefined);
        set_email(undefined);
        set_drawRequireApproval(undefined);
        set_validationAttempted(false);
    };

    useEffect(() => {
        if (!isOpen) return;

        // Force form remount to clear any component internal state
        set_formKey((prev) => prev + 1);

        if (!authorizedDrawUser) {
            resetFields();
            // Default "Require Approval" to true for new users
            set_drawRequireApproval(true);
            set_saveButtonText(SaveInvitedUserButtonTextOptions.SendInvite);
            // Enable validation immediately for new users (show asterisks on required fields)
            set_validationAttempted(true);
        } else {
            set_firstName(authorizedDrawUser.displayFirstName);
            set_lastName(authorizedDrawUser.displayLastName);
            set_roleType(authorizedDrawUser.displayRole);
            set_phone(authorizedDrawUser.displayPhone);
            set_email(authorizedDrawUser.email);
            set_drawRequireApproval(authorizedDrawUser.isApprovalRequired);
            set_saveButtonText(SaveInvitedUserButtonTextOptions.Save);
            // Enable validation immediately for edit mode (fields are already populated)
            set_validationAttempted(true);
        }
    }, [isOpen, authorizedDrawUser]);

    const handleReset = () => {
        resetFields();
        onClose();
    };

    const handleSave = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!validationGroupRef?.current?.instance) {
            throw new Error('A valid validation group reference is required to save');
        }

        // If this is first validation attempt, enable validation and wait for re-render
        if (!validationAttempted) {
            set_validationAttempted(true);
            setTimeout(() => {
                validationGroupRef?.current?.instance?.validate();
            }, 0);
            return false;
        }

        // Validate on subsequent attempts (validation is already enabled)
        const validationResult = validationGroupRef?.current?.instance?.validate();
        if (!validationResult.isValid) return false;

        const cleanEmail = email?.trim() ?? '';
        if (!cleanEmail) {
            GenericAlertPopup({ title: 'Error', children: 'Email address is required', width: '350px' });
            return false;
        }

        set_submitting(true);
        try {
            const success: boolean = await onSubmitAsync({
                email: cleanEmail,
                displayRole: roleType,
                displayFirstName: firstName,
                displayLastName: lastName,
                displayPhone: phone,
                isApprovalRequired: drawRequireApproval ?? false,
            });
            if (success) {
                handleReset();
            }
            return success;
        } finally {
            set_submitting(false);
        }
    };

    const actionButtons = [
        {
            id: 'cancelButton',
            theme: ButtonTheme.LinkBlue,
            disabled: submitting,
            children: 'Cancel',
            width: '100px',
            handleClick: handleReset,
        },
        {
            id: 'saveInvitedUser',
            theme: ButtonTheme.Primary,
            children: submitting ? 'Sending...' : saveButtonText,
            width: '160px',
            disabled: submitting,
            handleClick: handleSave,
        },
    ];

    const buildTitle = () => {
        return (
            <div className='userModal-title'>
                <div className='userModal-title title'>{authorizedDrawUser ? 'Edit User' : 'Invite User'}</div>
                <div className='userModal-title subheader'>
                    {mobileScreen
                        ? 'Pending acceptance, the user may change info'
                        : 'Pending acceptance, the user may change any information'}
                </div>
            </div>
        );
    };

    const toggleUserDrawRequireApprovalClick = (e: ButtonGroupTypes.ItemClickEvent) => {
        switch (e.itemData?.id.toLowerCase()) {
            case 'yes':
                set_drawRequireApproval(true);
                return;
            case 'no':
                set_drawRequireApproval(false);
                return;
            default:
                return;
        }
    };

    return (
        <>
            <GenericPopup
                id='inviteUserModal'
                isOpen={isOpen}
                actionButtons={actionButtons}
                handleClose={handleReset}
                aria-labelledby='alert-dialog-title'
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title={buildTitle()}
                width='500px'
                height={mobileScreen ? '99%' : '740px'}
                maxWidth='98%'
                maxHeight={mobileScreen ? '99%' : '740px'}
                buttonAlignment='center'
                wrapTitle={true}
                useCustomTitleRender={true}
            >
                <div className={`userModal ${mobileScreen ? 'mobile' : ''}`}>
                    <ValidationGroup
                        ref={validationGroupRef}
                        key={formKey}
                    >
                        <div style={{ height: mobileScreen ? '63vh' : 'auto' }}>
                            <TextBox
                                value={firstName}
                                onChange={(value) => set_firstName(value)}
                                label='First Name'
                                required={validationAttempted}
                                readOnly={submitting}
                            />
                            <TextBox
                                value={lastName}
                                onChange={(value) => set_lastName(value)}
                                label='Last Name'
                                required={validationAttempted}
                                readOnly={submitting}
                            />
                            <DropDownBox
                                id='userRoleDropdown'
                                value={roleType}
                                onChange={(value) => set_roleType(parseInt(value))}
                                required={validationAttempted}
                                disabled={submitting}
                                dataSource={GCRoleTypeOptions}
                                displayExpr={'name'}
                                valueExpr={'id'}
                                label='Role'
                            />
                            <PhoneBox
                                id='userPhone'
                                value={phone}
                                onChange={(value) => set_phone(value)}
                                label='Phone Number'
                                required={validationAttempted}
                                width='100%'
                                readOnly={submitting}
                            />
                            <TextBox
                                value={email}
                                onChange={(value) => set_email(value)}
                                label='Email Address'
                                required={validationAttempted}
                                readOnly={!!authorizedDrawUser || submitting}
                            />
                            <YesNoToggleButton
                                id='userDrawRequireApproval'
                                value={drawRequireApproval}
                                onItemClick={toggleUserDrawRequireApprovalClick}
                                width='230px'
                                height='40px'
                                required={validationAttempted}
                                requiredMessage='Require Approval required'
                                label='Require Approval'
                                labelSize={ToggleButtonLabelSize.Small}
                                disabled={submitting}
                            />
                        </div>
                    </ValidationGroup>
                </div>
            </GenericPopup>
        </>
    );
};
